import React, { useEffect, useState } from 'react';
import Papa from 'papaparse';
import FilterBar from './FilterBar';
import ProductCard from './ProductCard';
import SearchBar from './SearchBar';
import Pagination from './Pagination';
import InitiativePartners from './InitiativePartners';
import ProductLogosScroller from './ProductLogosScroller';
import SortBar from './SortBar';
import './ProductDirectory.css';

const CSV_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vSo2lBCCFE_d5vnZ1Fg38wbI3UMnVsyUZssyPugtF9vU-WLvGGBoxxUAq_D8L5ezPukVFuQvANn5bKW/pub?output=csv';

function trimRow(row) {
  const trimmed = {};
  Object.entries(row).forEach(([k, v]) => {
    trimmed[k.trim()] = typeof v === 'string' ? v.trim() : v;
  });
  return trimmed;
}

const FILTER_COLUMNS = [
  'Primary product Domain',
  'Product  Type',
  'Org Type'
];

const ProductDirectory = () => {
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [page, setPage] = useState(1);

  // Sort state
  const [sortOrder, setSortOrder] = useState('az');

  // Load and parse CSV from Google Sheets
  useEffect(() => {
    Papa.parse(CSV_URL, {
      download: true,
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const trimmedData = results.data.map(trimRow);
        const trimmedColumns = results.meta.fields.map(f => f.trim());
        setData(trimmedData);
        setColumns(trimmedColumns);
        setLoading(false);
      }
    });
  }, []);

  // Filtering logic
  const filteredData = data.filter(row =>
    FILTER_COLUMNS.every(col => {
      const filterVal = filters[col];
      if (!filterVal || filterVal.length === 0 || filterVal === 'All') return true;
      if (Array.isArray(filterVal)) {
        return filterVal.some(val =>
          (row[col] || '').toLowerCase().includes(val.toLowerCase())
        );
      }
      return (row[col] || '').toLowerCase().includes(filterVal.toLowerCase());
    })
  );

  // Search logic (case-insensitive, checks all main fields)
  const searchedData = filteredData.filter(row => {
    if (!search.trim()) return true;
    const searchLower = search.toLowerCase();
    return (
      (row['Organisation'] || '').toLowerCase().includes(searchLower) ||
      (row['Product/ Products'] || '').toLowerCase().includes(searchLower) ||
      (row['Product'] || '').toLowerCase().includes(searchLower) ||
      (row['Primary product Domain'] || '').toLowerCase().includes(searchLower) ||
      (row['Product  Type'] || '').toLowerCase().includes(searchLower) ||
      (row['Org Type'] || '').toLowerCase().includes(searchLower)
    );
  });

  // Sort logic
  const sortedData = [...searchedData].sort((a, b) => {
    const nameA = (a['Product'] || a['Product/ Products'] || '').toLowerCase();
    const nameB = (b['Product'] || b['Product/ Products'] || '').toLowerCase();
    if (sortOrder === 'az') {
      return nameA.localeCompare(nameB);
    } else {
      return nameB.localeCompare(nameA);
    }
  });

  // Pagination logic
  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedData = sortedData.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  // Stats for header
  const stats = {
    products: data.length,
    orgs: new Set(data.map(d => d['Organisation'])).size,
    dpgs: data.filter(d => (d['Product  Type'] || '').toLowerCase() === 'dpg').length,
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  // Reset page when filters/search/itemsPerPage change
  useEffect(() => {
    setPage(1);
  }, [filters, search, itemsPerPage, sortOrder]);

  // Extract logo URLs from the CSV data (assuming column name is 'Logo' or similar)
  const logoUrls = data
    .map(row => row['Logo'])
    .filter(url => url && url.startsWith('http'));

  return (
    <section className="product-directory">
      <div className="product-container">
        <div className="content-wrapper">
          <h1 className="main-title">C4GT Product Directory</h1>
          <p className="description">
            Organizations play a crucial role in advancing the mission of the community. The core of the community revolves around the growth of open-source products built by DPGs/DPI & GovTech organizations. By actively participating, organizations contribute to shaping a collaborative ecosystem and fostering innovation to create population-scale impact.
          </p>
          <div className="stats-container">
            <div className="stat-item">
              <div className="stat-number">{stats.products}+</div>
              <div className="stat-label">Products</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{stats.orgs}+</div>
              <div className="stat-label">Orgs</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{stats.dpgs}+</div>
              <div className="stat-label">DPGs</div>
            </div>
          </div>
        </div>
        <ProductLogosScroller logos={logoUrls} />
      </div>
      <div className="product-directory-content">
        {loading ? (
          <div className="product-directory-loading">Loading...</div>
        ) : (
          <>
            <FilterBar
              columns={columns}
              data={data}
              filters={filters}
              setFilters={setFilters}
              filterColumns={FILTER_COLUMNS}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 32px', marginBottom: 18 }}>
              <SearchBar
                search={search}
                setSearch={setSearch}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                totalItems={totalItems}
                page={page}
              />
              <SortBar sortOrder={sortOrder} setSortOrder={setSortOrder} />
            </div>
            <div className="product-cards-list">
              {paginatedData.length === 0 ? (
                <div className="product-directory-noresults">No results found.</div>
              ) : (
                paginatedData.map((row, idx) => (
                  <ProductCard key={idx} row={row} />
                ))
              )}
            </div>
            {totalPages > 1 && (
              <Pagination
                page={page}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
              />
            )}
            <InitiativePartners />
          </>
        )}
      </div>
    </section>
  );
};

export default ProductDirectory;